import { useState, useEffect } from 'react';
import { addSeconds } from 'date-fns/fp';

import api from '../helpers/http';
import history from '../history';
import getTokenData from '../helpers/getTokenData';
import UserService from '../services/User';
import isFranchiseeManagment from '../helpers/isFranchiseeManagment';
import { createPagedMeta } from '../entities/PagedMeta';
import { createOptionsPage } from '../entities/OptionsPaged';
import SchoolService from '../services/School';
import User from '../services/User';
import { getPushToken } from '../pushNotifications';
import { buildLoginPathWithRedirect } from '../helpers/safeRedirect';
import { resolvePostLoginPath } from '../helpers/pushNavigation';
import {
  disableBiometric as disableBiometricCredentials,
  enableBiometric as enableBiometricCredentials,
  getDeviceId,
  unlockDeviceToken,
} from '../helpers/biometricAuth';
import DeviceTokenService from '../services/DeviceToken';
import { Device } from '@capacitor/device';

export default function useAuth() {
  const storeKeyToken = 'x-access-token';
  const storeKeyTokenExpires = 'x-access-token-expires';
  const storeKeyUser = 'x-access-user';
  const storeAppVersion = 'x-access-app-version';
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appVersion, setAppVersion] = useState('');
  const [token, setToken] = useState('');
  const [tokenExpires, setTokenExpires] = useState(0);
  const [user, setUser] = useState({});

  useEffect(() => {
    const currentTokenExpires = Number(localStorage.getItem(storeKeyTokenExpires) || 0);
    if (currentTokenExpires) {
      setTokenExpires(currentTokenExpires);
    }

    const currentToken = localStorage.getItem(storeKeyToken);
    if (currentToken) {
      setAuthenticated(true);
      setToken(currentToken);
    }

    const currentUser = localStorage.getItem(storeKeyUser);
    if (currentUser) {
      setUser(JSON.parse(currentUser));
    }

    const currentAppVersion = localStorage.getItem(storeAppVersion);
    if (currentAppVersion) {
      setAppVersion(currentAppVersion);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (tokenExpires) {
      const now = new Date().getTime();
      if (now > tokenExpires) {
        handleSessionExpired();
      }
    }
  }, [tokenExpires]);

  const persistSession = (data: any) => {
    const resultToken = data?.token || '';
    localStorage.setItem(storeKeyToken, resultToken);
    setAuthenticated(true);
    setToken(resultToken);
    const version = data?.appVersion || 'HR';
    if (version) {
      setAppVersion(version);
      localStorage.setItem(storeAppVersion, version);
    }
    const resultTokenExpiresAt = Number(data?.expiresAt || 0);
    const now = new Date();
    const expiresTokenTimestamp = addSeconds(resultTokenExpiresAt, now).getTime();
    setTokenExpires(expiresTokenTimestamp);
    localStorage.setItem(storeKeyTokenExpires, String(expiresTokenTimestamp));

    setUser(getTokenData(resultToken));
    return resultToken;
  };

  const handleLogin = async (email: string, password: string, captcha: string) => {
    const pushToken = await getPushToken();
    try {
      const result = await api.post('/auth/login', {
        email,
        password,
        captcha,
        pushToken: pushToken?.token || '',
      });
      persistSession(result?.data?.data);
      return history.replace(resolvePostLoginPath());
    } catch (err: any) {
      console.error('Error: ', err);
      return { error: err?.response?.data?.message || 'Auth failed' };
    }
  };

  const handleBiometricLogin = async () => {
    const stored = await unlockDeviceToken();
    if (!stored) {
      return { cancelled: true };
    }

    try {
      const deviceId = await getDeviceId();
      const data = await DeviceTokenService('').loginPublic(stored.deviceToken, deviceId);
      persistSession(data);
      if (data?.deviceToken) {
        await enableBiometricCredentials(stored.email, data.deviceToken);
      }
      return history.replace(resolvePostLoginPath());
    } catch (err: any) {
      const status = Number(err?.response?.status || 0);
      const rejected = [401, 403, 422].includes(status);
      if (rejected) {
        await disableBiometricCredentials();
      }
      return { error: err?.response?.data?.message || 'Auth failed', disabled: rejected };
    }
  };

  const enableBiometricLogin = async (email: string) => {
    const deviceId = await getDeviceId();
    const sessionToken = localStorage.getItem(storeKeyToken) || '';
    if (!deviceId || !sessionToken) {
      return false;
    }
    try {
      const info = await Device.getInfo();
      const deviceToken = await DeviceTokenService(sessionToken).request(deviceId, info?.platform);
      return await enableBiometricCredentials(email, deviceToken);
    } catch (err: any) {
      return false;
    }
  };

  const disableBiometricLogin = async (currentToken?: string) => {
    const sessionToken = currentToken ?? localStorage.getItem(storeKeyToken) ?? '';
    await disableBiometricCredentials();
    const deviceId = await getDeviceId();
    if (deviceId && sessionToken) {
      await DeviceTokenService(sessionToken)
        .revoke(deviceId)
        .catch(() => false);
    }
    return true;
  };

  const handleCancelAccount = async () => {
    try {
      await User(token || '').cancelAccount();
      return true;
    } catch (err: any) {
      return { error: err?.response?.data?.message || 'Auth failed' };
    }
  };

  const handleLogout = () => {
    const sessionToken = localStorage.getItem(storeKeyToken) || '';
    disableBiometricLogin(sessionToken);
    handleClearToken();
    history.push('/login');
  };

  const handleSessionExpired = () => {
    handleClearToken();
    const attempted = `${window?.location?.pathname || ''}${window?.location?.search || ''}`;
    history.replace(buildLoginPathWithRedirect(attempted));
  };

  const handleClearToken = () => {
    setAuthenticated(false);
    localStorage.removeItem(storeKeyToken);
    localStorage.removeItem(storeKeyTokenExpires);
    localStorage.removeItem(storeKeyUser);
    setToken('');
  };

  const getProfile = async () => {
    const token = localStorage.getItem(storeKeyToken);
    if (token) {
      let user = getTokenData(token);
      const profile = await UserService(token).profile();
      user = profile?.data ? { ...user, ...profile.data } : user;
      localStorage.setItem(storeKeyUser, JSON.stringify(user));
      setUser(user);
      if (isFranchiseeManagment(user)) {
        const filteredOptions = {
          filters: [] as any[],
          options: createOptionsPage(createPagedMeta()),
        };
        const filterFranchiseeId = {
          field: 'franchiseeId',
          operation: 'hash',
          value: user.franchiseeId,
        };
        filteredOptions.filters = [filterFranchiseeId];
        const result = await SchoolService(token || '').list(filteredOptions);
        if (result.data.length === 0) {
          history.push('/initial-config');
        }
      }
    }
  };

  return {
    token,
    tokenExpires,
    user,
    authenticated,
    loading,
    appVersion,
    handleLogin,
    handleBiometricLogin,
    enableBiometricLogin,
    disableBiometricLogin,
    handleLogout,
    handleCancelAccount,
    handleClearToken,
    getProfile,
  };
}
